import { makeAutoObservable } from "mobx";
import { onError } from "src/common/onError";
import { msgQueueStore } from "src/components/MsgQueue";
import type { RtLoaderFn } from "src/components/ReactiveTable/RtLoader";
import { RtStore } from "src/components/ReactiveTable/RtStore";

import type { ZFilterMetadataLoader } from "./api/filterMetadataApi";
import { QueryBuilderStore } from "./QueryBuilderStore";
import type {
  ZFilterGroup,
  ZFilterTemplate,
  ZFilterTemplateCreate,
} from "./types/ZFilterApiTypes";
import { validationContexts } from "./types/ZValidationContext";

const version = "1";

type DraftSource = "localFilter" | "new" | "savedTemplate";

type QueryBuilderFiltersManagerParams = {
  metadataLoader: ZFilterMetadataLoader;
  setFilter: (filter: ZFilterGroup | undefined) => void;
  templatesLoader: RtLoaderFn<ZFilterTemplate, {}>;
  deleteTemplate: (filterId: string) => Promise<unknown>;
  createTemplate: (data: ZFilterTemplateCreate) => Promise<unknown>;
};

const getAutoFilterLabel = (index: number): string =>
  index > 0 ? `Фильтр (${index})` : "Фильтр";

const normalizeFilterName = (name: string): string =>
  name.trim().toLocaleLowerCase();

export class QueryBuilderFiltersManager {
  queryBuilderStore: QueryBuilderStore;

  setQueryBuilderStore(value: QueryBuilderStore): void {
    this.queryBuilderStore = value;
  }

  templatesTableStore: RtStore<ZFilterTemplate, {}> | null = null;

  setTemplatesTableStore(value: RtStore<ZFilterTemplate, {}> | null): void {
    this.templatesTableStore = value;
  }

  private createTemplatesTableStore(): RtStore<ZFilterTemplate, {}> {
    return new RtStore<ZFilterTemplate, {}>({
      rowKey: "id",
      loader: this.templatesLoader,
      selectionType: "radio",
      defaultRequest: {
        pageSize: 20,
      },
    });
  }

  isTemplatesModalOpen: boolean = false;

  setTemplatesModalOpen(value: boolean): void {
    if (value) {
      this.setTemplatesTableStore(this.createTemplatesTableStore());
    }
    this.isTemplatesModalOpen = value;
  }

  isSaveTemplateModalOpen: boolean = false;

  setSaveTemplateModalOpen(value: boolean): void {
    this.isSaveTemplateModalOpen = value;
  }

  isQueryBuilderModalOpen: boolean = false;

  setQueryBuilderModalOpen(value: boolean): void {
    this.isQueryBuilderModalOpen = value;
    if (!value) {
      this.setSaveTemplateModalOpen(false);
    }
  }

  filters: ZFilterTemplate[] = [];

  setFilters(list: ZFilterTemplate[]): void {
    this.filters = [...list];
  }

  activeFilterId: string | null = null;

  setActiveFilterId(value: string | null): void {
    this.activeFilterId = value;
  }

  private draftSource: DraftSource = "new";

  private setDraftSource(value: DraftSource): void {
    this.draftSource = value;
  }

  get canSaveAsTemplate(): boolean {
    if (this.draftSource === "savedTemplate") {
      return this.queryBuilderStore.draftStore.isDirty;
    }
    return true;
  }

  private readonly setFilter: (filter: ZFilterGroup | undefined) => void;

  private readonly templatesLoader: RtLoaderFn<ZFilterTemplate, {}>;

  private readonly deleteTemplateAction: (filterId: string) => Promise<unknown>;

  private readonly createTemplateAction: (
    data: ZFilterTemplateCreate,
  ) => Promise<unknown>;

  private isFilterNameUnique(name: string): boolean {
    const normalizedName = normalizeFilterName(name);

    if (!normalizedName) {
      return true;
    }

    return !this.filters.some(
      (item) =>
        item.id !== this.activeFilterId &&
        normalizeFilterName(item.name) === normalizedName,
    );
  }

  constructor(params: QueryBuilderFiltersManagerParams) {
    const {
      metadataLoader,
      setFilter,
      templatesLoader,
      deleteTemplate,
      createTemplate,
    } = params;
    this.queryBuilderStore = new QueryBuilderStore(metadataLoader);
    this.queryBuilderStore.draftStore.setNameUniquenessChecker((name) =>
      this.isFilterNameUnique(name),
    );
    this.templatesLoader = templatesLoader;
    this.deleteTemplateAction = deleteTemplate;
    this.createTemplateAction = createTemplate;
    this.setFilter = setFilter;
    makeAutoObservable(this);
  }

  async init(): Promise<void> {
    await this.queryBuilderStore.init();
  }

  dispose(): void {
    this.queryBuilderStore.dispose();
  }

  openNewDraft(): void {
    this.setActiveFilterId(null);
    this.setDraftSource("new");
    this.queryBuilderStore.reset();
    this.queryBuilderStore.draftStore.captureInitialDraft();
    this.setSaveTemplateModalOpen(false);
    this.setQueryBuilderModalOpen(true);
  }

  private loadDraftFromTemplate(
    filter: Pick<ZFilterTemplate, "description" | "filter" | "name">,
  ): void {
    this.queryBuilderStore.loadFromFilterGroup(filter.filter);
    this.queryBuilderStore.draftStore.setTemplateName(filter.name);
    this.queryBuilderStore.draftStore.setTemplateDescription(
      filter.description,
    );
    this.queryBuilderStore.draftStore.captureInitialDraft();
    this.setSaveTemplateModalOpen(false);
    this.setQueryBuilderModalOpen(true);
  }

  openFilterDraft(filterId: string): void {
    const storedFilter = this.filters.find((item) => item.id === filterId);
    if (!storedFilter) {
      return;
    }

    this.setActiveFilterId(filterId);
    this.setDraftSource("localFilter");
    this.loadDraftFromTemplate(storedFilter);
  }

  applyDraft(): boolean {
    if (
      !this.queryBuilderStore.draftStore.validate({
        context: validationContexts.apply,
      })
    ) {
      return false;
    }

    const filterGroup = this.queryBuilderStore.toFilterGroup();
    const filterName = this.queryBuilderStore.draftStore.templateName.trim();
    const filterDescription =
      this.queryBuilderStore.draftStore.templateDescription.trim();

    if (this.activeFilterId) {
      const index = this.filters.findIndex(
        (item) => item.id === this.activeFilterId,
      );
      if (index >= 0) {
        const current = this.filters[index];
        if (current) {
          const nextFilters = [...this.filters];
          nextFilters[index] = {
            ...current,
            filter: filterGroup,
            name: filterName,
            description: filterDescription,
          };
          this.setFilters(nextFilters);
        }
      }
    } else {
      this.setFilters([
        ...this.filters,
        {
          id: crypto.randomUUID(),
          name: filterName,
          description: filterDescription,
          filter: filterGroup,
          version,
        },
      ]);
    }

    this.applyCombinedFilter();
    this.queryBuilderStore.draftStore.commitDraftSnapshot();
    this.setQueryBuilderModalOpen(false);
    return true;
  }

  async saveAsTemplate() {
    if (
      !this.queryBuilderStore.draftStore.validate({
        context: validationContexts.saveTemplate,
      })
    ) {
      return;
    }

    this.setSaveTemplateModalOpen(true);
  }

  async confirmSaveAsTemplate() {
    if (
      !this.queryBuilderStore.draftStore.validate({
        context: validationContexts.saveTemplate,
      })
    ) {
      return;
    }

    try {
      await this.createTemplateAction({
        name: this.queryBuilderStore.draftStore.templateName.trim(),
        description:
          this.queryBuilderStore.draftStore.templateDescription.trim(),
        filter: this.queryBuilderStore.toFilterGroup(
          validationContexts.saveTemplate,
        ),
        version,
      });
      this.templatesTableStore?.reload();
      this.queryBuilderStore.draftStore.commitDraftSnapshot();
      this.setDraftSource("savedTemplate");
      this.setSaveTemplateModalOpen(false);
      msgQueueStore.add({
        type: "success",
        message: "Шаблон фильтра сохранён",
      });
    } catch (error) {
      onError(error);
    }
  }

  async deleteTemplate(id: string) {
    try {
      await this.deleteTemplateAction(id);
      this.templatesTableStore?.setSelectedKeys([]);
      this.templatesTableStore?.reload();
    } catch (error) {
      onError(error);
    }
  }

  get canSelectTemplate() {
    return this.templatesTableStore?.selectedRows
      ? this.templatesTableStore.selectedRows.length > 0
      : false;
  }

  removeFilter(filterId: string): void {
    this.setFilters(this.filters.filter((item) => item.id !== filterId));
    if (this.activeFilterId === filterId) {
      this.setActiveFilterId(null);
    }
    this.applyCombinedFilter();
  }

  selectTemplateFromModal(): void {
    const selectedTemplate = this.templatesTableStore?.selectedRows[0];
    if (!selectedTemplate) {
      return;
    }

    this.setActiveFilterId(null);
    this.setDraftSource("savedTemplate");
    this.loadDraftFromTemplate(selectedTemplate);
    this.setTemplatesModalOpen(false);
  }

  async resetCurrentFilter(): Promise<void> {
    if (this.activeFilterId) {
      this.removeFilter(this.activeFilterId);
      this.setQueryBuilderModalOpen(false);
      return;
    }

    this.setQueryBuilderModalOpen(false);
  }

  resetConditions(): void {
    this.queryBuilderStore.resetConditions();
  }

  get hasAppliedFilters(): boolean {
    return this.filters.length > 0;
  }

  get filterTags(): {
    key: string;
    label: string;
    onClick: () => void;
    onClose: () => void;
  }[] {
    let unnamedIndex = 0;

    return this.filters.map((item) => {
      const label = item.name || getAutoFilterLabel(unnamedIndex);
      if (!item.name) {
        unnamedIndex += 1;
      }

      return {
        key: item.id,
        label,
        onClick: () => this.openFilterDraft(item.id),
        onClose: () => this.removeFilter(item.id),
      };
    });
  }

  private applyCombinedFilter(): void {
    if (this.filters.length === 0) {
      this.setFilter(undefined);
      return;
    }

    if (this.filters.length === 1) {
      const single = this.filters[0];
      this.setFilter(single?.filter);
      return;
    }

    this.setFilter({
      op: "AND",
      items: this.filters.map((item) => item.filter),
    });
  }
}
